import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  MatListModule,
  MatSelectionList,
} from '@angular/material/list';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { Destroyable } from '../../utils/mixins/destroyable.mixin';
import { HttpHeaders } from '@angular/common/http';
import { COMPONENT_ID_HEADER } from '../../core/consts/api.const';
import { LoadingService } from '../../services/loading.service';
import { RockImagesService } from '../../services/rock-images/rock-images.service';
import {
  ConfirmPopupComponent,
  ConfirmPopupData,
} from '../../common/popups/confirm-popup/confirm-popup.component';
import { UploadRockImagePopupComponent } from '../../common/popups/upload-rock-image-popup/upload-rock-image-popup.component';
import { UploadRockImageData } from '../../core/models/upload-rock-image.model';
import { MatDialog } from '@angular/material/dialog';
import { SnackbarService } from '../../services/snackbar.service';
import { catchError, concatMap, EMPTY, Observable, tap, throwError } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { RemoveExtensionPipe } from '../../common/pipes/remove-extension.pipe';
import { ViewPhasesPopupComponent } from '../../common/popups/view-phases-popup/view-phases-popup.component';
import { Router } from '@angular/router';
import { UpscalingRockFile } from '../../core/models/upscaling-rock-files.model';

@Component({
  selector: 'app-upscaling-rock-images',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatInputModule,
    MatButtonModule,
    FormsModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatListModule,
    MatIconModule,
    MatTableModule,
    RemoveExtensionPipe,
  ],
  templateUrl: './upscaling-rock-images.component.html',
  styleUrl: './upscaling-rock-images.component.scss',
  standalone: true,
})
export class UpscalingRockImagesComponent extends Destroyable(Object) implements OnInit {
  @ViewChild('rockFilesList') rockFilesList!: MatSelectionList;

  private readonly componentId: string = 'upscaling-rock-images';
  isLoading = false;

  rockFiles: UpscalingRockFile[] = [];
  filteredRockFiles: UpscalingRockFile[] = [];
  selectedFile: UpscalingRockFile | null = null;
  searchInput = '';

  constructor(
    private readonly dialog: MatDialog,
    private readonly loadingService: LoadingService,
    private readonly rockImagesService: RockImagesService,
    private readonly snackbarService: SnackbarService,
    private readonly router: Router,
  ) {
    super();
  }

  ngOnInit(): void {
    this.subscribeToLoadingState();
    this.initRockFiles();
  }

  filterRockImageFiles(): void {
    this.filteredRockFiles = this.rockFiles.filter((f: UpscalingRockFile) =>
      f.fileName.toLowerCase().includes(this.searchInput.toLowerCase()),
    );
  }

  onRowClick(file: UpscalingRockFile): void {
    if (this.selectedFile?.fileName === file.fileName) {
      this.selectedFile = null;
    } else {
      this.selectedFile = file;
    }
  }

  clearListSelection(): void {
    this.selectedFile = null;
  }

  openUploadDialog(): void {
    const dialogData: UploadRockImageData = {
      fileFormat: 'raw',
      file: null,
    };

    const dialogRef = this.dialog.open(UploadRockImagePopupComponent, {
      minWidth: '500px',
      maxWidth: '700px',
      data: dialogData,
      disableClose: false,
    });

    dialogRef
      .afterClosed()
      .pipe(
        concatMap((result: UploadRockImageData | null) => {
          if (!result) return EMPTY;

          return this.uploadRockImageFile(result).pipe(
            tap(() => {
              this.showViewPhasesDialog(result);
            }),
          );
        }),
        this.takeUntilDestroyed(),
      )
      .subscribe();
  }

  showViewPhasesDialog(uploadData: UploadRockImageData): void {
    const fileName = uploadData.file?.name || '';

    this.dialog.open(ViewPhasesPopupComponent, {
      maxWidth: '90vw',
      maxHeight: '90vh',
      panelClass: 'stretch-dialog',
      data: { fileName },
    });
  }

  openViewPhasesDialogForSelectedFile(): void {
    if (!this.selectedFile) return;

    this.dialog.open(ViewPhasesPopupComponent, {
      maxWidth: '90vw',
      maxHeight: '90vh',
      panelClass: 'stretch-dialog',
      data: { fileName: this.selectedFile.fileName, rockFile: this.selectedFile },
    });
  }

  navigateToRockModels(): void {
    if (this.selectedFile) {
      this.router.navigate(['/rock-models'], {
        queryParams: { fileName: this.selectedFile.fileName }
      });
    } else {
      this.router.navigate(['/rock-models']);
    }
  }

  uploadRockImageFile(uploadData: UploadRockImageData): Observable<any> {
    if (!uploadData.file) return EMPTY;

    const fileNameWithoutExtension = new RemoveExtensionPipe().transform(uploadData.file.name);
    const fileAlreadyExists = this.rockFiles.some((f) => f.fileName === uploadData.file!.name);

    if (fileAlreadyExists) {
      this.snackbarService.openCustom({
        message: `A file named "${fileNameWithoutExtension}" already exists`,
        title: 'Error',
        type: 'error',
        action: 'Close',
      });
      return EMPTY;
    }

    const options = {
      headers: new HttpHeaders({
        [COMPONENT_ID_HEADER]: this.componentId,
      }),
    };

    this.snackbarService.openCustom({
      message: `File uploading started: "${fileNameWithoutExtension}"`,
      title: 'Info',
      type: 'info',
      action: 'Close',
    });

    this.loadingService.startLoading(this.componentId);

    return this.rockImagesService
      .uploadNewRockFile(uploadData, options, 'Upscaling')
      .pipe(
        tap(() => {
          this.snackbarService.openCustom({
            message: `File "${fileNameWithoutExtension}" uploaded successfully`,
            title: 'Success',
            type: 'success',
            action: 'Close',
          });
        }),
        concatMap(() => this.loadAvailableImages()),
        this.takeUntilDestroyed(),
      );
  }


  confirmDeleteRockImageFile(): void {
    if (!this.selectedFile) return;

    const displayName = new RemoveExtensionPipe().transform(this.selectedFile.fileName);
    const dialogData: ConfirmPopupData = {
      message: `Do you want to delete the file "${displayName}"?`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
    };

    const dialogRef = this.dialog.open(ConfirmPopupComponent, {
      minWidth: '500px',
      maxWidth: '700px',
      minHeight: '200px',
      data: dialogData,
    });

    dialogRef
      .afterClosed()
      .pipe(
        concatMap((result) => (result ? this.deleteRockImageFile() : EMPTY)),
        this.takeUntilDestroyed(),
      )
      .subscribe();
  }

  deleteRockImageFile(): Observable<UpscalingRockFile[]> {
    return this.rockImagesService.deleteRockFileImage(this.selectedFile!.fileName, 'Upscaling').pipe(
      tap(() => {
        const deletedName = new RemoveExtensionPipe().transform(this.selectedFile?.fileName ?? '');
        this.snackbarService.openCustom({
          message: `File ${deletedName} deleted successfully`,
          title: 'Success',
          type: 'success',
          action: 'Close',
        });
        this.selectedFile = null;
      }),
      concatMap(() => this.loadAvailableImages()),
    );
  }

  private initRockFiles(): void {
    this.loadingService.startLoading(this.componentId);
    this.loadAvailableImages().subscribe();
  }

  private loadAvailableImages(): Observable<UpscalingRockFile[]> {
    const options = {
      headers: new HttpHeaders({
        [COMPONENT_ID_HEADER]: this.componentId,
      }),
    };

    return this.rockImagesService
      .getRockFilesData(options, 'Upscaling')
      .pipe(
        tap((files) => {
          const upscalingFiles = files as UpscalingRockFile[];
          // Add numberOfPhases if not provided by backend
          const processedFiles = upscalingFiles.map(file => ({
            ...file,
            numberOfPhases: file.numberOfPhases ||
              (file.fractionHistogram ? Object.keys(file.fractionHistogram).length : undefined)
          }));
          this.rockFiles = [...processedFiles];
          this.filteredRockFiles = [...processedFiles];
          this.loadingService.stopLoading(this.componentId);
        }),
        this.takeUntilDestroyed(),
      );
  }

  private subscribeToLoadingState(): void {
    this.loadingService
      .getLoadingState(this.componentId)
      .pipe(this.takeUntilDestroyed())
      .subscribe((loadingState: boolean) => {
        this.isLoading = loadingState;
      });
  }
}
